import { type FC } from "react";

import { useMutation } from "@apollo/client/react";

import {
  type DocumentFragment,
  ProjectDocumentDetachDocument,
  type ProjectFragment,
} from "@app_schema";

import { Button } from "@styled/button";
import { Modal } from "@styled/modal";
import { ModalClose } from "@styled/modal_close";
import { ModalContent } from "@styled/modal_content";
import { ModalFooter } from "@styled/modal_footer";
import { ModalHeader } from "@styled/modal_header";
import { ModalTitle } from "@styled/modal_title";

export const DocumentDestroyDialog: FC<{
  project: ProjectFragment;
  document: DocumentFragment;
  cascading?: boolean;
  onDestroy?(): void;
  onClose(): void;
}> = ({ project, document, cascading, onDestroy, onClose }) => {
  const [execute, { loading }] = useMutation(ProjectDocumentDetachDocument);

  const onConfirm = async () => {
    await execute({
      variables: {
        projectID: project.id,
        documentID: document.id,
      },
    });
    onClose();
    onDestroy?.();
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader>
        <ModalTitle>
          {cascading ? "Delete processed records?" : "Delete document?"}
        </ModalTitle>
        <ModalClose onClose={onClose} />
      </ModalHeader>
      <ModalContent>
        {cascading
          ? "This will permanently remove this document, its processing run, and all generated results (record review, medical summary, and index). You'll need to upload and process records again."
          : "This will permanently remove this document from the project."}
      </ModalContent>
      <ModalFooter>
        <Button
          type="button"
          color="slate"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color="rose"
          onClick={onConfirm}
          loading={loading}
        >
          {cascading ? "Delete Document & Results" : "Delete"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
